import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from './SessionContext';
import { supabase } from './supabaseClient';
import {
  loadPilotCompliance,
  savePilotCompliance,
} from './pilotStatus';
import wingitDesign from './designSystem';

const MIN_OPERATIONAL_YEAR = 2000;
const MAX_OPERATIONAL_YEAR = 2100;

function displayValue(value) {
  return value && String(value).trim()
    ? value
    : 'Not provided';
}

function isMissing(value) {
  return (
    !value ||
    !String(value).trim() ||
    value === '-'
  );
}

function getTodayDateString() {
  const now = new Date();

  const localDate = new Date(
    now.getTime() - now.getTimezoneOffset() * 60000
  );

  return localDate.toISOString().split('T')[0];
}

function getDateYear(value) {
  const match = String(value || '').match(
    /^(\d+)-\d{2}-\d{2}$/
  );

  if (!match) return null;

  return Number(match[1]);
}

function getOperationalDateError(value, label) {
  if (!value) return '';

  const text = String(value).trim();

  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const yearText = text.split('-')[0] || text;

    return `Please check the ${label.toLowerCase()} year. ${yearText} does not appear to be valid.`;
  }

  const year = getDateYear(text);

  if (
    !Number.isInteger(year) ||
    year < MIN_OPERATIONAL_YEAR ||
    year > MAX_OPERATIONAL_YEAR
  ) {
    return `Please check the ${label.toLowerCase()} year. It must be between ${MIN_OPERATIONAL_YEAR} and ${MAX_OPERATIONAL_YEAR}.`;
  }

  const parsed = new Date(`${text}T00:00:00`);

  if (!Number.isFinite(parsed.getTime())) {
    return `Please enter a valid ${label.toLowerCase()} date.`;
  }

  return '';
}

function isPastDate(value) {
  if (!value) return false;

  if (
    getOperationalDateError(value, 'Date')
  ) {
    return false;
  }

  return value < getTodayDateString();
}

function formatDateForMessage(value) {
  if (!value) return '';

  const parsed = new Date(`${value}T00:00:00`);

  if (!Number.isFinite(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const session = useSession();
  const user = session?.user;
  const userMeta = user?.user_metadata || {};
  const styles = getStyles();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);

  const [profileForm, setProfileForm] = useState({
    full_name: '',
    phone: '',
    licence_type: '',
    home_airfield: '',
  });

  const [compliance, setCompliance] = useState({
    licenceExpiry: '',
    medicalExpiry: '',
    lastLandingDate: '',
    nightCurrencyExpiry: '',
    checkFlightDue: '',
  });

  const [statusMsg, setStatusMsg] = useState('');

  useEffect(() => {
    async function loadProfile() {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('users')
        .select(
          'full_name, email, phone, role, licence_type, org_name, home_airfield, licence_expiry, medical_expiry, last_landing_date, night_currency_expiry, check_flight_due'
        )
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        setStatusMsg(
          `Could not load profile: ${error.message}`
        );
      }

      setProfile(data || null);

      setProfileForm({
        full_name:
          data?.full_name ||
          userMeta.full_name ||
          '',
        phone:
          data?.phone ||
          userMeta.phone ||
          '',
        licence_type:
          data?.licence_type ||
          userMeta.licence_type ||
          '',
        home_airfield:
          data?.home_airfield ||
          userMeta.home_airfield ||
          '',
      });

      const savedCompliance = loadPilotCompliance(
        user.id
      );

      setCompliance({
        licenceExpiry:
          data?.licence_expiry ||
          savedCompliance.licenceExpiry ||
          userMeta.licence_expiry ||
          '',
        medicalExpiry:
          data?.medical_expiry ||
          savedCompliance.medicalExpiry ||
          userMeta.medical_expiry ||
          '',
        lastLandingDate:
          data?.last_landing_date ||
          savedCompliance.lastLandingDate ||
          '',
        nightCurrencyExpiry:
          data?.night_currency_expiry ||
          savedCompliance.nightCurrencyExpiry ||
          '',
        checkFlightDue:
          data?.check_flight_due ||
          savedCompliance.checkFlightDue ||
          '',
      });

      setLoading(false);
    }

    loadProfile();
  }, [
    user?.id,
    userMeta.full_name,
    userMeta.phone,
    userMeta.licence_type,
    userMeta.home_airfield,
    userMeta.licence_expiry,
    userMeta.medical_expiry,
  ]);

  const fullName =
    profileForm.full_name ||
    profile?.full_name ||
    userMeta.full_name ||
    '';

  const email =
    profile?.email ||
    user?.email ||
    '';

  const phone =
    profileForm.phone ||
    profile?.phone ||
    userMeta.phone ||
    '';

  const role =
    profile?.role ||
    userMeta.role ||
    '';

  const licence =
    profileForm.licence_type ||
    profile?.licence_type ||
    userMeta.licence_type ||
    '';

  const orgName =
    profile?.org_name ||
    userMeta.org_name ||
    '';

  const homeAirfield =
    profileForm.home_airfield ||
    profile?.home_airfield ||
    userMeta.home_airfield ||
    '';

  const dateErrors = useMemo(() => {
    return {
      licenceExpiry: getOperationalDateError(
        compliance.licenceExpiry,
        'Licence expiry'
      ),

      medicalExpiry: getOperationalDateError(
        compliance.medicalExpiry,
        'Medical expiry'
      ),

      nightCurrencyExpiry:
        getOperationalDateError(
          compliance.nightCurrencyExpiry,
          'Night currency expiry'
        ),

      checkFlightDue: getOperationalDateError(
        compliance.checkFlightDue,
        'Check flight due'
      ),
    };
  }, [
    compliance.licenceExpiry,
    compliance.medicalExpiry,
    compliance.nightCurrencyExpiry,
    compliance.checkFlightDue,
  ]);

  const hasDateErrors = Object.values(
    dateErrors
  ).some(Boolean);

  const profileChecks = useMemo(() => {
    return [
      {
        label: 'Full name',
        value: fullName,
      },
      {
        label: 'Phone',
        value: phone,
      },
      {
        label: 'Licence type',
        value: licence,
      },
      {
        label: 'Home airfield',
        value: homeAirfield,
      },
      {
        label: 'Licence expiry',
        value: compliance.licenceExpiry,
      },
      {
        label: 'Medical expiry',
        value: compliance.medicalExpiry,
      },
    ];
  }, [
    fullName,
    phone,
    licence,
    homeAirfield,
    compliance.licenceExpiry,
    compliance.medicalExpiry,
  ]);

  const dateWarnings = useMemo(() => {
    const warnings = [];

    if (isPastDate(compliance.licenceExpiry)) {
      warnings.push(
        `Your licence expiry date, ${formatDateForMessage(
          compliance.licenceExpiry
        )}, has already passed.`
      );
    }

    if (isPastDate(compliance.medicalExpiry)) {
      warnings.push(
        `Your medical expiry date, ${formatDateForMessage(
          compliance.medicalExpiry
        )}, has already passed.`
      );
    }

    if (
      isPastDate(compliance.nightCurrencyExpiry)
    ) {
      warnings.push(
        `Your night currency expiry date, ${formatDateForMessage(
          compliance.nightCurrencyExpiry
        )}, has already passed.`
      );
    }

    if (isPastDate(compliance.checkFlightDue)) {
      warnings.push(
        `Your check flight due date, ${formatDateForMessage(
          compliance.checkFlightDue
        )}, has already passed.`
      );
    }

    return warnings;
  }, [
    compliance.licenceExpiry,
    compliance.medicalExpiry,
    compliance.nightCurrencyExpiry,
    compliance.checkFlightDue,
  ]);

  const missingItems = profileChecks.filter(
    (item) => isMissing(item.value)
  );

  const completedItems =
    profileChecks.length - missingItems.length;

  const completionPercent = Math.round(
    (completedItems / profileChecks.length) * 100
  );

  function handleProfileChange(event) {
    const { name, value } = event.target;

    setProfileForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setStatusMsg('');
  }

  function handleComplianceChange(event) {
    const { name, value } = event.target;

    setCompliance((prev) => ({
      ...prev,
      [name]: value,
    }));

    setStatusMsg('');
  }

  async function handleSaveAll() {
    if (!user?.id || saving) return;

    setStatusMsg('');

    if (hasDateErrors) {
      setStatusMsg(
        'Please correct the highlighted date before saving.'
      );

      return;
    }

    setSaving(true);

    const profileUpdates = {
      full_name:
        profileForm.full_name.trim() || null,
      phone:
        profileForm.phone.trim() || null,
      licence_type:
        profileForm.licence_type.trim() || null,
      home_airfield:
        profileForm.home_airfield
          .trim()
          .toUpperCase() || null,
    };

    const complianceUpdates = {
      licence_expiry:
        compliance.licenceExpiry || null,
      medical_expiry:
        compliance.medicalExpiry || null,
      last_landing_date:
        compliance.lastLandingDate || null,
      night_currency_expiry:
        compliance.nightCurrencyExpiry || null,
      check_flight_due:
        compliance.checkFlightDue || null,
    };

    const { error } = await supabase
      .from('users')
      .update({
        ...profileUpdates,
        ...complianceUpdates,
      })
      .eq('id', user.id);

    if (error) {
      setSaving(false);

      setStatusMsg(
        `Could not save profile: ${error.message}`
      );

      return;
    }

    setProfile((prev) => ({
      ...(prev || {}),
      ...profileUpdates,
      ...complianceUpdates,
      email,
      role,
      org_name: orgName,
    }));

    savePilotCompliance(user.id, compliance);

    window.dispatchEvent(
      new Event('wingit-pilot-status-updated')
    );

    setSaving(false);
    navigate('/dashboard');
  }

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.eyebrow}>
            PROFILE
          </div>

          <h1 style={styles.title}>
            Your pilot profile
          </h1>

          <p style={styles.subtitle}>
            Keep your details and operational
            compliance up to date.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          style={styles.secondaryButton}
        >
          Back to Dashboard
        </button>
      </div>

      {loading ? (
        <div style={styles.loading}>
          Loading profile...
        </div>
      ) : (
        <>
          <section style={styles.panel}>
            <h2 style={styles.panelTitle}>
              Personal details
            </h2>

            <p style={styles.muted}>
              These details help your club identify
              and contact you.
            </p>

            <div style={styles.formGrid}>
              <TextField
                label="Full name"
                name="full_name"
                value={profileForm.full_name}
                onChange={handleProfileChange}
                styles={styles}
              />

              <TextField
                label="Phone"
                name="phone"
                value={profileForm.phone}
                onChange={handleProfileChange}
                styles={styles}
              />

              <TextField
                label="Licence type"
                name="licence_type"
                value={profileForm.licence_type}
                onChange={handleProfileChange}
                placeholder="e.g. PPL, LAPL, NPPL, SPL"
                styles={styles}
              />

              <TextField
                label="Home airfield"
                name="home_airfield"
                value={profileForm.home_airfield}
                onChange={(event) => {
                  setProfileForm((prev) => ({
                    ...prev,
                    home_airfield:
                      event.target.value.toUpperCase(),
                  }));

                  setStatusMsg('');
                }}
                placeholder="e.g. EGBG"
                styles={styles}
              />
            </div>

            <div style={styles.readOnlyGrid}>
              <Detail
                label="Email"
                value={displayValue(email)}
                missing={isMissing(email)}
                styles={styles}
              />

              <Detail
                label="Role"
                value={displayValue(role)}
                missing={isMissing(role)}
                styles={styles}
              />

              <Detail
                label="Organisation"
                value={displayValue(orgName)}
                missing={isMissing(orgName)}
                styles={styles}
              />
            </div>
          </section>

          <section style={styles.panel}>
            <h2 style={styles.panelTitle}>
              Operational compliance
            </h2>

            <p style={styles.muted}>
              These dates help Wingit highlight
              licence, medical and currency items.
            </p>

            <div style={styles.formGrid}>
              <DateField
                label="Licence expiry"
                name="licenceExpiry"
                value={compliance.licenceExpiry}
                onChange={handleComplianceChange}
                warning={isPastDate(
                  compliance.licenceExpiry
                )}
                error={dateErrors.licenceExpiry}
                styles={styles}
              />

              <DateField
                label="Medical expiry"
                name="medicalExpiry"
                value={compliance.medicalExpiry}
                onChange={handleComplianceChange}
                warning={isPastDate(
                  compliance.medicalExpiry
                )}
                error={dateErrors.medicalExpiry}
                styles={styles}
              />

              <DateField
                label="Last landing date"
                name="lastLandingDate"
                value={compliance.lastLandingDate}
                onChange={handleComplianceChange}
                styles={styles}
              />

              <DateField
                label="Night currency expiry"
                name="nightCurrencyExpiry"
                value={
                  compliance.nightCurrencyExpiry
                }
                onChange={handleComplianceChange}
                warning={isPastDate(
                  compliance.nightCurrencyExpiry
                )}
                error={
                  dateErrors.nightCurrencyExpiry
                }
                styles={styles}
              />

              <DateField
                label="Check flight due"
                name="checkFlightDue"
                value={compliance.checkFlightDue}
                onChange={handleComplianceChange}
                warning={isPastDate(
                  compliance.checkFlightDue
                )}
                error={dateErrors.checkFlightDue}
                styles={styles}
              />
            </div>

            {dateWarnings.length > 0 && (
              <div style={styles.warningBox}>
                <div style={styles.warningTitle}>
                  Action required
                </div>

                {dateWarnings.map((warning) => (
                  <div
                    key={warning}
                    style={styles.warningItem}
                  >
                    {warning}
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={handleSaveAll}
              style={{
                ...styles.primaryButton,
                ...(saving || hasDateErrors
                  ? styles.disabledButton
                  : {}),
              }}
              disabled={saving || hasDateErrors}
            >
              {saving
                ? 'Saving...'
                : 'Save and return to Dashboard'}
            </button>

            {statusMsg && (
              <div
                style={
                  statusMsg.startsWith(
                    'Could not'
                  ) ||
                  statusMsg.startsWith(
                    'Please correct'
                  )
                    ? styles.errorText
                    : styles.successText
                }
              >
                {statusMsg}
              </div>
            )}
          </section>

          <section style={styles.panel}>
            <div style={styles.completionHeader}>
              <div>
                <h2 style={styles.panelTitle}>
                  Profile progress
                </h2>

                <p style={styles.muted}>
                  {completedItems} of{' '}
                  {profileChecks.length} key items
                  completed
                </p>
              </div>

              <div style={styles.percent}>
                {completionPercent === 100
                  ? '✓ Complete'
                  : `${completionPercent}%`}
              </div>
            </div>

            <div style={styles.progressOuter}>
              <div
                style={{
                  ...styles.progressInner,
                  width: `${completionPercent}%`,
                }}
              />
            </div>

            {missingItems.length > 0 ? (
              <div style={styles.noticeBox}>
                <span style={styles.noticeLabel}>
                  Still needed:
                </span>{' '}
                {missingItems
                  .map((item) => item.label)
                  .join(', ')}
              </div>
            ) : hasDateErrors ? (
              <div style={styles.errorProgressBox}>
                Your profile contains an invalid date.
                Correct the highlighted field before
                saving.
              </div>
            ) : dateWarnings.length > 0 ? (
              <div style={styles.warningProgressBox}>
                Your profile is complete, but one or
                more operational dates require
                attention.
              </div>
            ) : (
              <div style={styles.successBox}>
                You're all set. Wingit will now
                monitor your licence, medical and
                flying currency automatically.
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Detail({
  label,
  value,
  missing,
  styles,
}) {
  return (
    <div style={styles.detailRow}>
      <div style={styles.detailLabel}>
        {label}
      </div>

      <div
        style={
          missing
            ? styles.missingValue
            : styles.detailValue
        }
      >
        {missing ? 'Not provided' : value}
      </div>
    </div>
  );
}

function TextField({
  label,
  name,
  value,
  onChange,
  placeholder,
  styles,
}) {
  return (
    <label style={styles.field}>
      <div style={styles.label}>
        {label}
      </div>

      <input
        type="text"
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder || ''}
        style={styles.input}
      />
    </label>
  );
}

function DateField({
  label,
  name,
  value,
  onChange,
  warning = false,
  error = '',
  styles,
}) {
  return (
    <label style={styles.field}>
      <div style={styles.label}>
        {label}
      </div>

      <input
        type="date"
        name={name}
        value={value}
        onChange={onChange}
        min={
          name === 'lastLandingDate'
            ? undefined
            : `${MIN_OPERATIONAL_YEAR}-01-01`
        }
        max={
          name === 'lastLandingDate'
            ? undefined
            : `${MAX_OPERATIONAL_YEAR}-12-31`
        }
        style={{
          ...styles.input,
          ...(warning
            ? styles.warningInput
            : {}),
          ...(error
            ? styles.errorInput
            : {}),
        }}
      />

      {error ? (
        <div style={styles.fieldError}>
          {error}
        </div>
      ) : warning ? (
        <div style={styles.fieldWarning}>
          This date has already passed.
        </div>
      ) : null}
    </label>
  );
}

function getStyles() {
  const ds = wingitDesign;

  return {
    page: {
      maxWidth: 1040,
      margin: '2.5rem auto',
      padding: '0 1rem 2rem',
      color: ds.colors.text,
    },

    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '1rem',
      alignItems: 'flex-start',
      marginBottom: '1rem',
      flexWrap: 'wrap',
    },

    eyebrow: {
      ...ds.typography.caption,
      color: ds.colors.primary,
      fontWeight: 800,
      marginBottom: '0.35rem',
    },

    title: {
      ...ds.typography.pageTitle,
      color: ds.colors.text,
      margin: 0,
      fontSize: '1.7rem',
      fontWeight: 750,
      letterSpacing: '-0.03em',
    },

    subtitle: {
      ...ds.typography.body,
      color: ds.colors.textMuted,
      margin: '0.4rem 0 0',
      maxWidth: 620,
    },

    panel: {
      ...ds.cards.secondary,
      borderRadius: ds.radius.large,
      padding: '1.15rem',
      marginTop: '1rem',
      textAlign: 'left',
      boxShadow: ds.shadow.card,
    },

    panelTitle: {
      ...ds.typography.cardTitle,
      color: ds.colors.text,
      margin: 0,
      fontSize: '1.05rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },

    muted: {
      ...ds.typography.body,
      color: ds.colors.textMuted,
      margin: '0.3rem 0 0',
      fontSize: '0.9rem',
    },

    completionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '1rem',
      alignItems: 'center',
    },

    percent: {
      color: ds.colors.primary,
      fontSize: '1rem',
      fontWeight: 750,
    },

    progressOuter: {
      marginTop: '0.9rem',
      width: '100%',
      height: 8,
      background: 'rgba(12,22,36,0.75)',
      borderRadius: 999,
      overflow: 'hidden',
      border: `1px solid ${ds.colors.border}`,
    },

    progressInner: {
      height: '100%',
      background: ds.colors.primary,
      borderRadius: 999,
    },

    noticeBox: {
      marginTop: '0.9rem',
      color: ds.colors.textMuted,
      background: 'rgba(242,184,75,0.08)',
      border:
        '1px solid rgba(242,184,75,0.22)',
      borderRadius: ds.radius.medium,
      padding: '0.75rem',
      fontSize: '0.88rem',
      lineHeight: 1.4,
    },

    noticeLabel: {
      color: ds.colors.text,
      fontWeight: 700,
    },

    successBox: {
      marginTop: '0.9rem',
      color: '#b7ffdc',
      background: 'rgba(47,211,125,0.08)',
      border:
        '1px solid rgba(47,211,125,0.24)',
      borderRadius: ds.radius.medium,
      padding: '0.75rem',
      fontSize: '0.88rem',
      lineHeight: 1.4,
    },

    warningProgressBox: {
      marginTop: '0.9rem',
      color: '#ffd98a',
      background: 'rgba(242,184,75,0.08)',
      border:
        '1px solid rgba(242,184,75,0.28)',
      borderRadius: ds.radius.medium,
      padding: '0.75rem',
      fontSize: '0.88rem',
      lineHeight: 1.4,
    },

    errorProgressBox: {
      marginTop: '0.9rem',
      color: '#ffb3b3',
      background: 'rgba(255,77,77,0.08)',
      border:
        '1px solid rgba(255,77,77,0.24)',
      borderRadius: ds.radius.medium,
      padding: '0.75rem',
      fontSize: '0.88rem',
      lineHeight: 1.4,
    },

    formGrid: {
      display: 'grid',
      gridTemplateColumns:
        'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '0.85rem',
      marginTop: '1rem',
    },

    field: {
      display: 'block',
    },

    label: {
      color: ds.colors.textMuted,
      fontSize: '0.82rem',
      fontWeight: 650,
      marginBottom: '0.35rem',
    },

    input: {
      width: '100%',
      padding: '0.65rem 0.72rem',
      borderRadius: ds.radius.medium,
      border: `1px solid ${ds.colors.border}`,
      background: 'rgba(12,22,36,0.72)',
      color: ds.colors.text,
      boxSizing: 'border-box',
      fontSize: '0.9rem',
      outline: 'none',
    },

    warningInput: {
      border:
        '1px solid rgba(242,184,75,0.75)',
      background: 'rgba(242,184,75,0.08)',
    },

    errorInput: {
      border:
        '1px solid rgba(242,109,109,0.85)',
      background: 'rgba(242,109,109,0.08)',
    },

    fieldWarning: {
      color: '#ffd98a',
      fontSize: '0.76rem',
      marginTop: '0.35rem',
    },

    fieldError: {
      color: '#ff9d9d',
      fontSize: '0.76rem',
      marginTop: '0.35rem',
      lineHeight: 1.35,
    },

    warningBox: {
      marginTop: '1rem',
      color: '#ffd98a',
      background: 'rgba(242,184,75,0.08)',
      border:
        '1px solid rgba(242,184,75,0.28)',
      borderRadius: ds.radius.medium,
      padding: '0.8rem',
      fontSize: '0.86rem',
      lineHeight: 1.45,
    },

    warningTitle: {
      color: ds.colors.text,
      fontWeight: 750,
      marginBottom: '0.35rem',
    },

    warningItem: {
      marginTop: '0.2rem',
    },

    primaryButton: {
      ...ds.buttons.primary,
      marginTop: '1rem',
      fontSize: '0.84rem',
      padding: '0.72rem 1rem',
    },

    disabledButton: {
      opacity: 0.55,
      cursor: 'not-allowed',
    },

    secondaryButton: {
      ...ds.buttons.secondary,
      fontSize: '0.84rem',
      padding: '0.72rem 1rem',
    },

    readOnlyGrid: {
      display: 'grid',
      gridTemplateColumns:
        'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '0.6rem',
      marginTop: '1rem',
      borderTop: `1px solid ${ds.colors.border}`,
      paddingTop: '1rem',
    },

    detailRow: {
      padding: '0.55rem 0',
    },

    detailLabel: {
      color: ds.colors.textMuted,
      fontSize: '0.75rem',
      marginBottom: '0.2rem',
    },

    detailValue: {
      color: ds.colors.text,
      fontSize: '0.9rem',
      fontWeight: 650,
    },

    missingValue: {
      color: ds.colors.textMuted,
      fontSize: '0.9rem',
      fontWeight: 550,
    },

    successText: {
      color: '#7df0a8',
      marginTop: '0.75rem',
      fontSize: '0.88rem',
    },

    errorText: {
      color: '#ff9d9d',
      marginTop: '0.75rem',
      fontSize: '0.88rem',
    },

    loading: {
      color: ds.colors.textMuted,
      padding: '1rem',
    },
  };
}